import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Action } from 'redux';

import { RevenueVerificationStatus } from '../../../domain/qualification';
import { DataLoadState, ErrorType, initialDataLoadState } from '../../../store/reducers/common';
import { continueApplicationProcessing } from '../../application/reducers/application';
import { BankAccountVerificationSuccessEvent } from '../../bank-account-connection/hooks/post-message-listener/PostMessageListener';

type BankAccountLinkingState = {
  widgetUrl: string | null;
  revenueVerificationStatus?: RevenueVerificationStatus | null;
} & DataLoadState;

const initialState: BankAccountLinkingState = {
  ...initialDataLoadState,
  widgetUrl: null,
  revenueVerificationStatus: null,
};

const slice = createSlice({
  name: 'verify-revenue/bank-account',
  initialState,
  reducers: {
    getBankAccountLinkingWidgetUrl: (state) => ({
      ...state,
      loading: true,
      error: null,
      widgetUrl: null,
    }),
    getBankAccountLinkingWidgetUrlSuccess: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      loading: false,
      error: null,
      widgetUrl: payload,
    }),
    getBankAccountLinkingWidgetUrlFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => ({
      ...state,
      loading: false,
      error: { message },
    }),
    notifyBankAccountConnected: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<{
        event: BankAccountVerificationSuccessEvent;
        onSuccess: Action;
      }>,
    ) => ({
      ...state,
      loading: true,
      error: null,
    }),
    notifyBankAccountConnectedSuccess: (state) => ({
      ...state,
      loading: false,
      error: null,
    }),
    notifyBankAccountConnectedFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => ({
      ...state,
      loading: false,
      error: { message },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      continueApplicationProcessing,
      (
        state,
        {
          payload: {
            response: { applicationMeta },
          },
        },
      ) => ({
        ...state,
        revenueVerificationStatus: applicationMeta?.revenueVerificationStatus || null,
      }),
    );
  },
});

const bankAccountLinkingReducer = slice.reducer;
const {
  getBankAccountLinkingWidgetUrl,
  getBankAccountLinkingWidgetUrlFail,
  getBankAccountLinkingWidgetUrlSuccess,
  notifyBankAccountConnectedSuccess,
  notifyBankAccountConnectedFail,
  notifyBankAccountConnected,
} = slice.actions;

export {
  bankAccountLinkingReducer,
  getBankAccountLinkingWidgetUrl,
  getBankAccountLinkingWidgetUrlFail,
  getBankAccountLinkingWidgetUrlSuccess,
  notifyBankAccountConnectedSuccess,
  notifyBankAccountConnectedFail,
  notifyBankAccountConnected,
};
